import {
  csvRowsToImportPayload,
  parseSimpleContactsCsv,
  parseSimpleContactsJson,
  postContactsDirectoryOp,
  type ContactImportRow,
  type ExternalContactRow,
} from "@/lib/contactsDirectoryClient";
import type { ImportMatch } from "./adminContactsDirectoryShared";

type Toast = (msg: string, variant?: "ok" | "err") => void;

type Params = {
  organizationId: string;
  external: ExternalContactRow[];
  normalizeEmail: (value: string | null | undefined) => string;
  normalizePhone: (value: string | null | undefined) => string;
  formatUnknownError: (err: unknown) => string;
  toast: Toast;
  load: () => Promise<void>;
  setPendingImportRows: (rows: ContactImportRow[]) => void;
  setImportMatches: (rows: ImportMatch[] | ((prev: ImportMatch[]) => ImportMatch[])) => void;
  pendingImportRows: ContactImportRow[];
  importMatches: ImportMatch[];
  setApplyingImport: (v: boolean) => void;
};

function toImportRow(r: ContactImportRow) {
  return {
    name: r.name,
    email: r.email,
    phone: r.phone,
    company: r.company,
    title: r.title,
    department: r.department,
    contact_path: r.contact_path,
    visibility_level: r.visibility_level,
    location_name: r.location_name,
    location_id: r.location_id,
    scope: r.scope,
    topics: r.topics,
    affiliation: r.affiliation || undefined,
    hierarchy_participation: r.hierarchy_participation || undefined,
    is_active: r.is_active === "false" || String(r.is_active).toLowerCase() === "false" ? false : true,
  };
}

export function useAdminContactsImportWorkflow(p: Params) {
  async function onImportSelected(file: File | null) {
    if (!file) return;
    try {
      const lower = file.name.toLowerCase();
      const text = await file.text();
      const parsedRows: ContactImportRow[] =
        lower.endsWith(".json") ? parseSimpleContactsJson(text) : csvRowsToImportPayload(parseSimpleContactsCsv(text));
      const activeExternal = p.external.filter((r) => r.is_active);
      const matches: ImportMatch[] = [];
      for (let i = 0; i < parsedRows.length; i++) {
        const incoming = parsedRows[i];
        const incomingEmail = p.normalizeEmail(incoming.email);
        const incomingPhone = p.normalizePhone(incoming.phone);
        const existing = activeExternal.find((candidate) => {
          const emailMatch = incomingEmail && p.normalizeEmail(candidate.email) && p.normalizeEmail(candidate.email) === incomingEmail;
          const phoneMatch = incomingPhone && p.normalizePhone(candidate.phone) && p.normalizePhone(candidate.phone) === incomingPhone;
          return Boolean(emailMatch || phoneMatch);
        });
        if (!existing) continue;
        const reasonBits: string[] = [];
        if (incomingEmail && p.normalizeEmail(existing.email) === incomingEmail) reasonBits.push("email");
        if (incomingPhone && p.normalizePhone(existing.phone) === incomingPhone) reasonBits.push("phone");
        matches.push({
          key: `${existing.id}:${i}`,
          existing,
          incoming,
          resolution: "keep_old",
          mergedDraft: {
            ...incoming,
            name: existing.name || incoming.name,
            email: existing.email || incoming.email || "",
            phone: existing.phone || incoming.phone || "",
            company: existing.company || incoming.company || "",
            title: existing.title || incoming.title || "",
            department: existing.department || incoming.department || "",
            contact_path: existing.contact_path || incoming.contact_path || "",
            visibility_level: existing.visibility_level ?? incoming.visibility_level ?? "1",
            max_downstream_visibility_level: existing.max_downstream_visibility_level ?? incoming.max_downstream_visibility_level ?? "",
            location_id: existing.location_id || incoming.location_id || "",
            scope: existing.scope || incoming.scope || "org",
            topics: Array.isArray(existing.topics) ? (existing.topics as string[]).join(", ") : incoming.topics ?? "",
            is_active: existing.is_active,
            affiliation: (incoming.affiliation && String(incoming.affiliation).trim()) || String(existing.affiliation || "org_affiliated"),
            hierarchy_participation:
              (incoming.hierarchy_participation && String(incoming.hierarchy_participation).trim()) ||
              String(existing.hierarchy_participation || "full"),
          },
          matchReason: reasonBits.length ? reasonBits.join(" + ") : "email/phone",
        });
      }
      if (matches.length) {
        p.setPendingImportRows(parsedRows);
        p.setImportMatches(matches);
        p.toast(`Found ${matches.length} matched contact(s). Review before importing.`, "ok");
        return;
      }
      const out = (await postContactsDirectoryOp(p.organizationId, { op: "import_external_contacts", rows: parsedRows.map(toImportRow) })) as {
        created?: number;
        failed?: number;
        errors?: string[];
      };
      p.toast(`Import complete: ${out.created ?? 0} created, ${out.failed ?? 0} failed.`, (out.failed ?? 0) > 0 ? "err" : "ok");
      if (out.errors?.length) console.warn(out.errors);
      await p.load();
    } catch (e: unknown) {
      p.toast(p.formatUnknownError(e), "err");
    }
  }

  async function applyImportWithMatches() {
    if (!p.pendingImportRows.length) return;
    p.setApplyingImport(true);
    try {
      const matchedIndices = new Set(
        p.importMatches.map((m) => Number(m.key.split(":").pop())).filter((n) => Number.isInteger(n) && n >= 0)
      );
      const unmatchedRows = p.pendingImportRows.filter((_row, idx) => !matchedIndices.has(idx));
      let created = 0;
      let failed = 0;
      const errors: string[] = [];

      if (unmatchedRows.length) {
        const out = (await postContactsDirectoryOp(p.organizationId, {
          op: "import_external_contacts",
          rows: unmatchedRows.map(toImportRow),
        })) as { created?: number; failed?: number; errors?: string[] };
        created += out.created ?? 0;
        failed += out.failed ?? 0;
        if (out.errors?.length) errors.push(...out.errors);
      }

      for (const match of p.importMatches) {
        if (match.resolution === "keep_old") continue;
        const source = match.resolution === "keep_new" ? match.incoming : match.mergedDraft;
        try {
          await postContactsDirectoryOp(p.organizationId, {
            op: "external_upsert",
            id: match.existing.id,
            name: source.name,
            email: source.email || null,
            phone: source.phone || null,
            company: source.company || null,
            title: source.title || null,
            department: source.department || null,
            contact_path: source.contact_path || null,
            visibility_level: source.visibility_level ?? 1,
            max_downstream_visibility_level: source.max_downstream_visibility_level || null,
            location_id: source.location_id || null,
            scope: source.scope || "org",
            topics: Array.isArray(source.topics) ? source.topics : String(source.topics || "").split(",").map((t) => t.trim()).filter(Boolean),
            is_active: source.is_active === false || source.is_active === "false" ? false : true,
            affiliation: source.affiliation || "org_affiliated",
            hierarchy_participation: source.hierarchy_participation || "full",
            reports_to_external_id: null,
          });
        } catch (err: unknown) {
          failed += 1;
          errors.push(`Match for ${match.existing.name}: ${p.formatUnknownError(err)}`);
        }
      }
      p.toast(`Import complete: ${created} created, ${failed} failed.`, failed > 0 ? "err" : "ok");
      if (errors.length) console.warn(errors.slice(0, 50));
      p.setPendingImportRows([]);
      p.setImportMatches([]);
      await p.load();
    } catch (e: unknown) {
      p.toast(p.formatUnknownError(e), "err");
    } finally {
      p.setApplyingImport(false);
    }
  }

  return { onImportSelected, applyImportWithMatches };
}
